import { useState } from 'react'
import { motion } from 'framer-motion'
import { 
  LogIn, 
  Mail, 
  Lock, 
  Eye, 
  EyeOff,
  Shield,
  CheckCircle
} from 'lucide-react'
import { facebookApi } from '@/lib/api'
import toast from 'react-hot-toast'

interface LoginProps {
  updateAuthStatus: () => void
}

export default function Login({ updateAuthStatus }: LoginProps) {
  const [credentials, setCredentials] = useState({
    email: '',
    pass: ''
  })
  const [showPassword, setShowPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [loginStep, setLoginStep] = useState<'login' | 'checking' | 'success'>('login')
  

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!credentials.email || !credentials.pass) {
      toast.error('Bitte alle Felder ausfüllen')
      return
    }

    setIsLoading(true)
    setLoginStep('checking')

    try {
      // 🔐 SUPABASE-LOGIN MIT DOPPELTER AUTHENTIFIZIERUNG:
      // 1. Supabase-Check: Ob E-Mail in der User-Liste existiert
      // 2. API.mademyday.ai-Check: Ob E-Mail/Passwort-Kombination korrekt ist
      const supabaseLoginResponse = await facebookApi.loginWithSupabase({
        email: credentials.email,
        password: credentials.pass
      })
      
      if (supabaseLoginResponse.success) {
        // ✅ Doppelte Authentifizierung erfolgreich
        toast.success(`Willkommen, ${supabaseLoginResponse.user.full_name}!`)
        setLoginStep('success')
        
        // Benutzer-Informationen speichern
        localStorage.setItem('user', JSON.stringify(supabaseLoginResponse.user))
        localStorage.setItem('isAdmin', supabaseLoginResponse.user.role === 'admin' ? 'true' : 'false')
        
        // Kurze Verzögerung für bessere UX, dann Auth-Status aktualisieren
        setTimeout(() => {
          console.log('🚀 Aktualisiere Auth-Status...')
          updateAuthStatus() // Dies löst eine Re-Render in der App-Komponente aus
        }, 500)
        
        return
      } else {
        // ❌ Supabase-Login fehlgeschlagen
        toast.error(supabaseLoginResponse.message || 'Login fehlgeschlagen')
        setLoginStep('login')
      }
      
    } catch (error: any) {
      console.error('Login-Fehler:', error)
      
      // Detaillierte Fehlermeldungen basierend auf dem Backend-Response
      if (error.response?.status === 401) {
        if (error.response?.data?.detail === 'E-Mail-Adresse nicht autorisiert') {
          toast.error('E-Mail-Adresse nicht in der Benutzer-Liste')
        } else if (error.response?.data?.detail === 'Ungültige E-Mail oder Passwort') {
          toast.error('Ungültige E-Mail oder Passwort')
        } else if (error.response?.data?.detail === 'Benutzer ist deaktiviert') {
          toast.error('Benutzer ist deaktiviert')
        } else {
          toast.error('Anmeldung fehlgeschlagen - Überprüfen Sie Ihre Anmeldedaten')
        }
      } else if (error.response?.status === 500) {
        if (error.response?.data?.detail?.includes('Externe Authentifizierung')) {
          toast.error('Externe Authentifizierung nicht verfügbar - Bitte versuchen Sie es später erneut')
        } else {
          toast.error('Server-Fehler - Bitte versuchen Sie es später erneut')
        }
      } else {
        toast.error('Login fehlgeschlagen - Überprüfen Sie Ihre Anmeldedaten')
      }
      
      setLoginStep('login')
    } finally {
      setIsLoading(false)
    }
  }

  const handleInputChange = (field: 'email' | 'pass', value: string) => {
    setCredentials(prev => ({ ...prev, [field]: value }))
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-purple-900 flex items-center justify-center p-6">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="glass-card p-8 max-w-md w-full"
      >
        {/* Header */}
        <div className="text-center mb-8">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-4"
          >
            <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
              <Shield className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-3xl font-bold text-white mb-2">Facebook Dashboard</h1>
            <p className="text-white/60">Anmeldung erforderlich</p>
          </motion.div>
        </div>

        {/* Login Form */}
        {loginStep === 'login' && (
          <motion.form
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            onSubmit={handleLogin}
            className="space-y-6"
          >
            {/* Email Field */}
            <div className="space-y-2">
              <label className="text-white/80 text-sm font-medium flex items-center">
                <Mail className="w-4 h-4 mr-2" />
                E-Mail Adresse
              </label>
              <div className="relative">
                <input
                  type="email"
                  value={credentials.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  className="input-modern w-full pl-10"
                  placeholder="E-Mail eingeben"
                  required
                />
                <Mail className="w-4 h-4 text-white/40 absolute left-3 top-1/2 transform -translate-y-1/2" />
              </div>
            </div>

            {/* Password Field */}
            <div className="space-y-2">
              <label className="text-white/80 text-sm font-medium flex items-center">
                <Lock className="w-4 h-4 mr-2" />
                Passwort
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={credentials.pass}
                  onChange={(e) => handleInputChange('pass', e.target.value)}
                  className="input-modern w-full pl-10 pr-10"
                  placeholder="Passwort eingeben"
                  required
                />
                <Lock className="w-4 h-4 text-white/40 absolute left-3 top-1/2 transform -translate-y-1/2" />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-white/40 hover:text-white/60"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {/* Login Button */}
            <button
              type="submit"
              disabled={isLoading}
              className="btn-primary w-full flex items-center justify-center"
            >
              {isLoading ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              ) : (
                <LogIn className="w-4 h-4 mr-2" />
              )}
              {isLoading ? 'Anmelden...' : 'Anmelden'}
            </button>
          </motion.form>
        )}

        {/* Checking Permissions */}
        {loginStep === 'checking' && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-center space-y-6"
          >
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-400 mx-auto"></div>
          </motion.div>
        )}

        {/* Success */}
        {loginStep === 'success' && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-center space-y-6"
          >
            <div className="w-16 h-16 bg-green-500/20 border border-green-500/40 rounded-full flex items-center justify-center mx-auto">
              <CheckCircle className="w-8 h-8 text-green-400" />
            </div>
            <div>
              <h3 className="text-white font-medium mb-2">Anmeldung erfolgreich!</h3>
              <p className="text-white/60 text-sm">Weiterleitung zum Dashboard...</p>
            </div>
          </motion.div>
        )}
      </motion.div>
    </div>
  )
} 